import { Component } from '@theme/component';

/**
 * @typedef {Object} AxRevealCardsRefs
 * @property {HTMLUListElement} list
 * @property {HTMLElement & { swiper?: any; initialize?: () => void }} swiper
 */

/**
 * Reveal Cards section — refonte 2026.
 *
 * Pattern identique à ax-expandable-cards mais sans le changement de taille
 * (cards toujours égales). Au hover, la card hovered devient .is-active →
 * sa description fade in.
 *
 *   - <ul> en desktop (flex row, cards égales via flex: 1).
 *   - <swiper-container init="false"> rendu vide pour mobile.
 *   - En mobile : JS déplace le contenu des <li> vers <swiper-slide>, init Swiper.
 *   - En desktop : pas de Swiper, layout natif.
 *
 * @extends {Component<AxRevealCardsRefs>}
 */
class AxRevealCards extends Component {
  /** @type {MediaQueryList} */
  #mql;
  /** @type {() => void} */
  #onBreakpointChange;
  /** @type {boolean} */
  #isMobileMounted = false;

  connectedCallback() {
    super.connectedCallback();

    this.#mql = window.matchMedia('(max-width: 1199px)');
    this.#initActiveSync();
    this.#initAsync();
  }

  async #initAsync() {
    await customElements.whenDefined('swiper-container');
    if (!this.isConnected) return;

    this.#onBreakpointChange = () => this.#syncMobileMount();
    this.#mql.addEventListener('change', this.#onBreakpointChange);
    this.#syncMobileMount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mql?.removeEventListener('change', this.#onBreakpointChange);
    const sw = this.refs.swiper?.swiper;
    if (sw && !sw.destroyed) sw.destroy(true, true);
  }

  // ============ MOBILE MOUNT / UNMOUNT ============

  #syncMobileMount() {
    if (this.#mql.matches) this.#mountMobile();
    else this.#unmountMobile();
  }

  #mountMobile() {
    if (this.#isMobileMounted) return;
    const { list, swiper } = this.refs;
    if (!list || !swiper) return;

    for (const li of [...list.children]) {
      const slide = document.createElement('swiper-slide');
      slide.className = li.className;
      while (li.firstChild) slide.appendChild(li.firstChild);
      swiper.appendChild(slide);
    }
    list.replaceChildren();

    Object.assign(swiper, {
      slidesPerView: 1.35, // iso-maquette : à viewport 375, slide ≈ 250px → 375/250 = 1.5
      spaceBetween: 12,
      grabCursor: true,
      a11y: { enabled: true },
      watchOverflow: true,
      pagination: false,
    });
    swiper.initialize?.();
    this.#isMobileMounted = true;

    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
  }

  #unmountMobile() {
    if (!this.#isMobileMounted) return;
    const { list, swiper } = this.refs;
    if (!list || !swiper) return;

    const sw = swiper.swiper;
    if (sw && !sw.destroyed) sw.destroy(true, true);

    for (const slide of [...swiper.children]) {
      const li = document.createElement('li');
      li.className = slide.className;
      while (slide.firstChild) li.appendChild(slide.firstChild);
      list.appendChild(li);
    }
    swiper.replaceChildren();
    this.#isMobileMounted = false;
  }

  // ============ DESKTOP ACTIVE SYNC (hover swap) ============

  #initActiveSync() {
    const list = this.refs.list;
    if (!list) return;

    list.addEventListener('mouseover', (e) => {
      if (this.#mql?.matches) return;
      const slide = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target.closest('.ax-reveal-cards__slide') : null);
      if (!slide || slide.classList.contains('is-active')) return;
      for (const s of list.children) s.classList.remove('is-active');
      slide.classList.add('is-active');
    });
  }
}

customElements.define('ax-reveal-cards', AxRevealCards);
